/**
 * Testimonials block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS.
 * The card's own stylesheet is a separate bundle, wired by the `slate.uses`
 * declaration in block.json — see inc/assets.php.
 *
 * The row scrolls on its own: `scroll-snap-type` in index.scss already gives a
 * trackpad, a touchscreen and the arrow keys everything they need. What this
 * adds is the pair of buttons, and keeping their spent state in step with where
 * the row has got to — neither of which CSS can express.
 *
 * No slider library: there is no autoplay, no loop and no pagination here, and
 * the native scroller already handles the gesture better than one would.
 */

import './index.scss';

const sections = document.querySelectorAll('.testimonial-carousel');

if (sections.length) {
    const reveal =
        'IntersectionObserver' in window
            ? new IntersectionObserver(
                  (entries) => {
                      for (const entry of entries) {
                          if (entry.isIntersecting) {
                              entry.target.setAttribute('data-reveal', 'in');
                              reveal.unobserve(entry.target);
                          }
                      }
                  },
                  { rootMargin: '0px 0px -15% 0px' }
              )
            : null;

    for (const section of sections) {
        if (reveal) {
            section.setAttribute('data-reveal', 'pending');
            reveal.observe(section);
        }

        const track = section.querySelector('.testimonial-carousel__track');
        const previous = section.querySelector('.testimonial-carousel__arrow--prev');
        const next = section.querySelector('.testimonial-carousel__arrow--next');

        if (!track || !previous || !next) {
            continue;
        }

        /**
         * Mark each arrow according to whether the row can still travel that
         * way. The one-pixel tolerance absorbs the fractional scroll widths a
         * zoomed or scaled page produces.
         *
         * @returns {void}
         */
        const sync = () => {
            const room = track.scrollWidth - track.clientWidth;

            // Three testimonials on a wide screen have nowhere to go. Rather
            // than show two permanently dimmed pills, the controls step aside
            // until the row can actually travel — which, on a narrower screen
            // or with a fourth card added, it can.
            section.setAttribute('data-slider', room > 1 ? 'on' : 'off');

            previous.setAttribute('aria-disabled', String(track.scrollLeft <= 1));
            next.setAttribute('aria-disabled', String(track.scrollLeft >= room - 1));
        };

        /**
         * Move by one card, letting scroll-snap settle the exact landing.
         *
         * @param {HTMLElement} control Button that was activated.
         * @param {number}      delta   -1 for back, 1 for forward.
         * @returns {void}
         */
        const move = (control, delta) => {
            if ('true' === control.getAttribute('aria-disabled')) {
                return;
            }

            const slide = track.querySelector('.testimonial-carousel__slide');
            const step = slide
                ? slide.getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap || 0)
                : track.clientWidth;

            track.scrollBy({ left: step * delta, behavior: 'smooth' });
        };

        previous.addEventListener('click', () => move(previous, -1));
        next.addEventListener('click', () => move(next, 1));
        track.addEventListener('scroll', sync, { passive: true });

        // The row's width changes with the viewport, and so does how far it can
        // travel — the arrows have to be told.
        if ('ResizeObserver' in window) {
            new ResizeObserver(sync).observe(track);
        }

        sync();
    }
}
